import { apiMutate } from "~/utils/apiClient";

const { VITE_CONVERSATIONS_API } = import.meta.env;
export const AGENTS_API_PREFIX = `${VITE_CONVERSATIONS_API}/agents`;
export const DEFAULT_NEW_AGENT_DISPLAY_NAME = "New Agent";

export const agentListPath = () => `${AGENTS_API_PREFIX}`;
export const agentPath = (id: string) => `${AGENTS_API_PREFIX}/${id}`;
export const agentDuplicatePath = (sourceId: string) =>
  `${AGENTS_API_PREFIX}/${sourceId}/duplicate`;
export const toolkitsPath = () => `${VITE_CONVERSATIONS_API}/toolkits`;
export const agentToolkitsPath = (agentId: string) => `${agentPath(agentId)}/toolkits`;
export const agentToolkitPath = (agentId: string, toolkitId: string) =>
  `${agentToolkitsPath(agentId)}/${toolkitId}`;
export const connectorsPath = () => `${VITE_CONVERSATIONS_API}/connectors`;
export const agentConnectorsPath = (agentId: string) => `${agentPath(agentId)}/connectors`;
export const agentConnectorPath = (agentId: string, connectorId: string) =>
  `${agentConnectorsPath(agentId)}/${connectorId}`;

export const AGENT_BRAND_COLORS: string[] = [
  "#685BC7", // manifold
  "#201547", // deep_space
  "#C5B4E3", // moonglow
  "#FF6A39", // forge_fire
  "#2D68C4", // hot_blue
  "#5A5B5D", // graphite
];

export interface Toolkit {
  id: string;
  display_name: string;
  description?: string;
}

export interface AgentToolkit {
  toolkit: Toolkit;
  created_at: string;
  created_by: string;
}

export const ConnectorSource = {
  SYSTEM: "SYSTEM",
  USER: "USER",
} as const;
export type ConnectorSource = (typeof ConnectorSource)[keyof typeof ConnectorSource];

export interface Connector {
  id: string;
  source: ConnectorSource;
  name: string;
  /** Human-readable label for the UI. Nullable; callers fall back to `name`
   * (the wire-safe dotted identifier) when this is absent. */
  display_name: string | null;
  description?: string;
  url: string;
}

export interface AgentConnector {
  connector: Connector;
  created_at: string;
  created_by: string | null;
}

export const AgentStatus = {
  DISABLED: "DISABLED",
  ENABLED: "ENABLED",
  DELETED: "DELETED",
} as const;
export type AgentStatus = (typeof AgentStatus)[keyof typeof AgentStatus];

export const AgentScope = {
  SYSTEM: "SYSTEM",
  CUSTOMER: "CUSTOMER",
} as const;
export type AgentScope = (typeof AgentScope)[keyof typeof AgentScope];

export interface AgentStarterPrompt {
  id: string;
  position: number;
  text: string;
  created_by: string | null;
  created_at: string;
}

export interface AgentFeaturedConversation {
  id: string;
  position: number;
  label: string;
  url: string;
  created_by: string | null;
  created_at: string;
}

export interface AgentStarterPromptInput {
  text: string;
}

export interface AgentFeaturedConversationInput {
  label: string;
  url: string;
}

export interface Agent {
  id: string;
  name: string;
  display_name: string;
  description: string;
  scope: AgentScope;
  customer_id: string | null;
  persona: string;
  model: string;
  status: AgentStatus;
  parent_agent_id: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  starter_prompts: AgentStarterPrompt[];
  featured_conversations: AgentFeaturedConversation[];
}

export const MAX_STARTER_PROMPTS = 6;
export const MAX_FEATURED_CONVERSATIONS = 6;
export const STARTER_PROMPT_MAX_LENGTH = 300;
export const FEATURED_CONVERSATION_LABEL_MAX_LENGTH = 100;
export const FEATURED_CONVERSATION_URL_MAX_LENGTH = 2048;

export type PartialAgent = Omit<Agent, "persona">;

export interface AgentListMetadata {
  limit: number;
  offset: number;
  total_count: number;
  has_more: boolean;
}

export interface AgentList {
  items: Agent[];
  metadata: AgentListMetadata;
}

export interface UpdateAgentInput {
  display_name?: string;
  description?: string;
  persona?: string;
  status?: AgentStatus;
  starter_prompts?: AgentStarterPromptInput[];
  featured_conversations?: AgentFeaturedConversationInput[];
}

export async function createAgent(): Promise<Agent> {
  return apiMutate<Agent>(agentListPath(), "POST", {
    display_name: DEFAULT_NEW_AGENT_DISPLAY_NAME,
  });
}

export async function duplicateAgent(sourceId: string): Promise<Agent> {
  return apiMutate<Agent>(agentDuplicatePath(sourceId), "POST");
}

export async function updateAgent(id: string, input: UpdateAgentInput): Promise<Agent> {
  return apiMutate<Agent>(agentPath(id), "PATCH", input);
}

export async function deleteAgent(id: string): Promise<void> {
  await apiMutate(agentPath(id), "DELETE");
}

export async function enableAgentToolkit(agentId: string, toolkitId: string): Promise<void> {
  await apiMutate(agentToolkitsPath(agentId), "POST", { toolkit_id: toolkitId });
}

export async function disableAgentToolkit(agentId: string, toolkitId: string): Promise<void> {
  await apiMutate(agentToolkitPath(agentId, toolkitId), "DELETE", undefined, { expects204: true });
}

export async function enableAgentConnector(agentId: string, connectorId: string): Promise<void> {
  await apiMutate(agentConnectorsPath(agentId), "POST", { connector_id: connectorId });
}

export async function disableAgentConnector(agentId: string, connectorId: string): Promise<void> {
  await apiMutate(agentConnectorPath(agentId, connectorId), "DELETE", undefined, {
    expects204: true,
  });
}
